import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/apiClient';
import Breadcrumb from '../ui/Breadcrumb';
import Modal from '../ui/Modal';
import ContextMenu from '../ui/ContextMenu';
import Spinner from '../ui/Spinner';
import { IconAlertTriangle, IconCheck, IconClose, IconDownload, IconEdit, IconFolder, IconGlobe, IconImage, IconPlus, IconTable, IconTrash } from '../ui/icons';
import DbSteps from '../ui/DbSteps';
import Skeleton from '../ui/Skeleton';

function useApiBaseUrl() {
  const raw = process.env.REACT_APP_API_BASE_URL;
  if (raw && raw.trim()) return raw.replace(/\/+$/, '');
  return '/api';
}

function mediaInlineUrl(apiBaseUrl, siteId, id) {
  const base = String(apiBaseUrl || '/api').replace(/\/+$/, '');
  const qs = new URLSearchParams({
    site_id: String(Number(siteId)),
    id: String(Number(id)),
  });
  return `${base}/media.php?${qs.toString()}`;
}

export default function GalleryPage({ siteId, site }) {
  const apiBaseUrl = useApiBaseUrl();
  const fileInputRef = useRef(null);

  const [state, setState] = useState({
    status: 'loading',
    dbConfig: null,
    ensureOk: false,
    categories: [],
    tags: [],
    items: [],
    error: '',
  });

  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [modal, setModal] = useState(null);
  const [siteMedia, setSiteMedia] = useState({ status: 'idle', error: '', assets: [] });
  const [ctxMenu, setCtxMenu] = useState(null);
  const [moveMode, setMoveMode] = useState(null); // { itemId }
  const [listLoading, setListLoading] = useState(false);

  const closeCtxMenu = useCallback(() => setCtxMenu(null), []);
  const openCtxMenu = useCallback((e, items) => {
    if (!e) return;
    e.preventDefault?.();
    e.stopPropagation?.();
    setCtxMenu({
      x: Number(e.clientX) || 0,
      y: Number(e.clientY) || 0,
      items: Array.isArray(items) ? items : [],
    });
  }, []);

  const refresh = useCallback(() => {
    if (!siteId) return;
    let cancelled = false;
    setListLoading(true);
    setState((s) => ({ ...s, status: 'loading', error: '' }));
    api
      .dbConnections(Number(siteId))
      .then(async (dbRes) => {
        if (cancelled) return;
        if (!dbRes.ok) {
          setListLoading(false);
          setState((s) => ({ ...s, status: 'error', error: dbRes.error || 'Impossible de charger la config BDD' }));
          return;
        }

        const dbConfig = (dbRes.connections || [])[0] || null;
        setState((s) => ({ ...s, dbConfig, status: 'ready' }));

        if (!dbConfig) {
          setListLoading(false);
          setState((s) => ({ ...s, ensureOk: false, categories: [], tags: [], items: [], error: '' }));
          return;
        }

        const listRes = await api.galleryList(Number(siteId), {
          category_id: selectedCategoryId ? Number(selectedCategoryId) : undefined,
          limit: 600,
        });
        if (cancelled) return;
        if (!listRes.ok) {
          setListLoading(false);
          setState((s) => ({
            ...s,
            ensureOk: false,
            categories: [],
            tags: [],
            items: [],
            error: listRes.status === 409 ? listRes.error : listRes.error || '',
          }));
          return;
        }
        setListLoading(false);
        setState((s) => ({
          ...s,
          ensureOk: true,
          categories: listRes.categories || [],
          tags: listRes.tags || [],
          items: listRes.items || [],
          error: '',
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setListLoading(false);
        setState((s) => ({ ...s, status: 'error', error: 'Impossible de charger' }));
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, selectedCategoryId]);

  useEffect(() => refresh(), [refresh]);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    setSiteMedia((s) => ({ ...s, status: 'loading', error: '' }));
    api
      .mediaList(Number(siteId))
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setSiteMedia({ status: 'error', error: res.error || 'Chargement impossible', assets: [] });
          return;
        }
        setSiteMedia({ status: 'ready', error: '', assets: res.assets || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setSiteMedia({ status: 'error', error: 'Chargement impossible', assets: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const mediaById = useMemo(() => {
    const m = new Map();
    for (const a of siteMedia.assets || []) {
      if (!a || a.id == null) continue;
      m.set(String(a.id), a);
    }
    return m;
  }, [siteMedia.assets]);

  const categories = useMemo(() => (Array.isArray(state.categories) ? state.categories : []), [state.categories]);
  const items = useMemo(() => (Array.isArray(state.items) ? state.items : []), [state.items]);

  const dbLoading = state.status === 'loading';
  const dbOk = !dbLoading && Boolean(state.dbConfig && state.ensureOk);
  const dbNeedsAttention = !dbLoading && Boolean(!state.dbConfig || (state.dbConfig && !state.ensureOk));
  const dbLabel = dbLoading ? 'Chargement…' : !state.dbConfig ? 'Connexion manquante' : state.ensureOk ? 'OK' : 'Tables manquantes';

  function downloadZipUrl(ids) {
    const list = (ids || []).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
    const qs = new URLSearchParams({
      site_id: String(Number(siteId || 0)),
      ids: list.join(','),
      download: '1',
    });
    return `${String(apiBaseUrl || '/api').replace(/\/+$/, '')}/media.php?${qs.toString()}`;
  }

  function filenameFromContentDisposition(headerValue) {
    const raw = String(headerValue || '').trim();
    if (!raw) return '';
    const star = raw.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (star && star[1]) {
      try {
        return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
      } catch {
        return star[1].trim().replace(/^"|"$/g, '');
      }
    }
    const m = raw.match(/filename\s*=\s*([^;]+)/i);
    if (!m || !m[1]) return '';
    return String(m[1]).trim().replace(/^"|"$/g, '');
  }

  async function downloadZip(ids) {
    const list = (ids || []).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
    if (!list.length) return;
    const url = downloadZipUrl(list);
    let res = null;
    try {
      res = await fetch(url, { credentials: 'include' });
    } catch {
      setState((s) => ({ ...s, error: 'Téléchargement impossible (réseau/CORS)' }));
      return;
    }
    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    if (!res.ok || contentType.includes('application/json')) {
      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }
      setState((s) => ({ ...s, error: data?.error || `Téléchargement zip impossible (HTTP ${res.status})` }));
      return;
    }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    const filename = filenameFromContentDisposition(cd) || 'galerie.zip';
    const a = document.createElement('a');
    const objUrl = URL.createObjectURL(blob);
    a.href = objUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
  }

  async function downloadAllForCategory(categoryId) {
    const cid = categoryId == null ? null : Number(categoryId);
    const res = await api.galleryList(Number(siteId), {
      category_id: cid == null ? undefined : cid,
      limit: 5000,
    });
    if (!res.ok) {
      setState((s) => ({ ...s, error: res.error || 'Téléchargement impossible' }));
      return;
    }
    const mediaIds = (res.items || [])
      .map((it) => Number(it?.media_id))
      .filter((n) => Number.isFinite(n) && n > 0);
    await downloadZip(mediaIds);
  }

  async function moveItemToCategory(itemId, categoryId) {
    const id = Number(itemId);
    if (!Number.isFinite(id) || id <= 0) return;
    const cid = categoryId == null ? null : Number(categoryId);
    const res = await api.galleryItemUpdate({
      site_id: Number(siteId),
      id,
      category_id: cid == null || cid <= 0 ? null : cid,
    });
    if (!res.ok) {
      setState((s) => ({ ...s, error: res.error || 'Déplacement impossible' }));
      return;
    }
    setMoveMode(null);
    refresh();
  }

  function openCategoryMenu(e, category) {
    const c = category && typeof category === 'object' ? category : null;
    const cid = c?.id != null ? Number(c.id) : null;
    const canManage = cid != null && Number.isFinite(cid) && cid > 0;
    openCtxMenu(e, [
      {
        key: 'download',
        label: 'Télécharger toutes les images',
        icon: <IconDownload />,
        onClick: () => downloadAllForCategory(canManage ? cid : null),
      },
      { type: 'sep', key: 'sep' },
      {
        key: 'rename',
        label: 'Renommer',
        icon: <IconEdit />,
        disabled: !canManage,
        onClick: () => {
          if (!canManage) return;
          setModal({ type: 'rename-category', id: cid, name: String(c?.name || ''), saving: false, error: '' });
        },
      },
      {
        key: 'delete',
        label: 'Supprimer',
        icon: <IconTrash />,
        variant: 'danger',
        disabled: !canManage,
        onClick: () => {
          if (!canManage) return;
          setModal({ type: 'delete-category', id: cid, name: String(c?.name || ''), deleting: false, error: '' });
        },
      },
    ]);
  }

  function openItemMenu(e, item) {
    const it = item && typeof item === 'object' ? item : null;
    const id = Number(it?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    openCtxMenu(e, [
      {
        key: 'move',
        label: 'Changer de catégorie',
        icon: <IconFolder />,
        onClick: () => setMoveMode({ itemId: id }),
      },
      { type: 'sep', key: 'sep' },
      {
        key: 'delete',
        label: 'Supprimer de la galerie',
        icon: <IconTrash />,
        variant: 'danger',
        onClick: async () => {
          const ok = window.confirm('Retirer cette photo de la galerie ?');
          if (!ok) return;
          const res = await api.galleryItemDelete({ site_id: Number(siteId), id });
          if (!res.ok) {
            setState((s) => ({ ...s, error: res.error || 'Suppression impossible' }));
            return;
          }
          refresh();
        },
      },
    ]);
  }

  async function ensureTables() {
    const res = await api.galleryEnsure(Number(siteId));
    if (!res.ok) return res;
    refresh();
    return res;
  }

  async function createCategory(name) {
    const t = String(name || '').trim();
    if (!t) return { ok: false, error: 'Nom requis' };
    const res = await api.galleryCategoryCreate({ site_id: Number(siteId), name: t });
    if (!res.ok) return res;
    refresh();
    return res;
  }

  async function uploadToGallery(files, categoryId) {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    const up = await api.mediaUpload(Number(siteId), arr);
    if (!up.ok) {
      setState((s) => ({ ...s, error: up.error || 'Upload impossible' }));
      return;
    }
    const createdIds = Array.isArray(up.created)
      ? up.created.map((c) => Number(c?.id)).filter((n) => Number.isFinite(n) && n > 0)
      : [];
    if (createdIds.length) {
      const res2 = await api.galleryAddMedia({
        site_id: Number(siteId),
        media_ids: createdIds,
        category_id: categoryId,
      });
      if (!res2.ok) {
        setState((s) => ({ ...s, error: res2.error || 'Ajout à la galerie impossible' }));
      }
    }
    refresh();
  }

  async function confirmRenameCategory() {
    if (!modal || modal.type !== 'rename-category') return;
    if (modal.saving) return;
    const name = String(modal.name || '').trim();
    if (!name) return;
    setModal((m) => (m ? { ...m, saving: true, error: '' } : m));
    const res = await api.galleryCategoryUpdate({ site_id: Number(siteId), id: Number(modal.id), name });
    if (!res.ok) {
      setModal((m) => (m ? { ...m, saving: false, error: res.error || 'Erreur' } : m));
      return;
    }
    setModal(null);
    refresh();
  }

  const siteName = String(site?.name || '').trim();

  function GalleryGridSkeleton() {
    return (
      <div
        style={{
          marginTop: 12,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 10,
        }}
        aria-label="Chargement des photos"
      >
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={`sk_${i}`} className="Tile" style={{ padding: 10 }}>
            <Skeleton variant="block" style={{ height: 160, borderRadius: 10 }} />
            <Skeleton variant="line" style={{ width: `${68 - i}%`, marginTop: 10 }} />
          </div>
        ))}
      </div>
    );
  }

  if (!siteId) {
    return (
      <div className="Page">
        <div className="Card">
          <div className="TileTitle">Galerie</div>
          <div className="Muted">Sélectionne un site.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={[{ label: 'Galerie' }]} />
          <div className="Badges">
            <button
              type="button"
              className={`Button Button--ghost ${dbOk ? 'Button--good' : dbNeedsAttention ? 'Button--danger' : ''} ${dbLoading ? 'Button--loading' : ''}`}
              disabled={false}
              onClick={() => setModal({ type: 'db-status', ensuring: false, error: '' })}
              title={`BDD Galerie — ${dbLabel}`}
            >
              <span className="ButtonInline">
                {dbLoading ? <Skeleton variant="circle" style={{ width: 16, height: 16 }} aria-hidden="true" /> : dbOk ? <IconCheck /> : dbNeedsAttention ? <IconAlertTriangle /> : <IconTable />}{' '}
                BDD Galerie
              </span>
            </button>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                e.target.value = '';
                const hasCats = categories.length > 0;
                const cid = selectedCategoryId ? Number(selectedCategoryId) : 0;
                if (hasCats && cid <= 0) {
                  setModal({ type: 'pick-upload-category', files });
                  return;
                }
                uploadToGallery(files, cid > 0 ? cid : null);
              }}
            />
            <button type="button" className="Button" disabled={!state.ensureOk} onClick={() => fileInputRef.current?.click?.()}>
              <span className="ButtonInline">
                <IconPlus /> Ajouter des photos
              </span>
            </button>
          </div>
        </div>
      </header>

      {state.error ? (
        <div className="Card" style={{ marginBottom: 14 }}>
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      {!state.dbConfig ? <div className="Hint">Connexion manquante: clique sur “BDD Galerie”.</div> : null}
      {state.dbConfig && !state.ensureOk ? <div className="Hint">Tables Galerie non initialisées: clique sur “BDD Galerie”.</div> : null}

      {state.ensureOk ? (
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 14 }}>
          <div className="Card" style={{ padding: 12 }}>
            <div className="TileTitle">Catégories</div>
            {moveMode ? (
              <div className="Notice" style={{ marginTop: 10 }}>
                <div className="ButtonInline" style={{ justifyContent: 'space-between', width: '100%' }}>
                  <span className="ButtonInline" style={{ gap: 10 }}>
                    <IconFolder /> Choisis une catégorie pour déplacer la photo
                  </span>
                  <button type="button" className="Button Button--ghost Button--danger Button--icon" onClick={() => setMoveMode(null)} title="Annuler" aria-label="Annuler">
                    <IconClose />
                  </button>
                </div>
              </div>
            ) : null}
            <div className="List" role="list" style={{ marginTop: 10 }}>
              <button
                type="button"
                className={`ListItem ListItem--button ${!selectedCategoryId ? 'is-active' : ''}`}
                onClick={() => {
                  if (moveMode) {
                    moveItemToCategory(moveMode.itemId, null);
                    return;
                  }
                  setSelectedCategoryId('');
                }}
                onContextMenu={(e) => openCategoryMenu(e, null)}
              >
                <span className="ButtonInline">
                  <IconImage /> Toutes les photos
                </span>
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`ListItem ListItem--button ${String(selectedCategoryId) === String(c.id) ? 'is-active' : ''}`}
                  onClick={() => {
                    if (moveMode) {
                      moveItemToCategory(moveMode.itemId, c.id);
                      return;
                    }
                    setSelectedCategoryId(String(c.id));
                  }}
                  onContextMenu={(e) => openCategoryMenu(e, c)}
                >
                  <span className="ButtonInline">
                    <IconFolder /> {c.name}
                  </span>
                </button>
              ))}
            </div>
            <div className="Badges" style={{ justifyContent: 'flex-start', marginTop: 10 }}>
              <button type="button" className="Button Button--ghost" onClick={() => setModal({ type: 'new-category', name: '', saving: false, error: '' })}>
                <span className="ButtonInline">
                  <IconPlus /> Créer une catégorie
                </span>
              </button>
            </div>
          </div>

	          <div className="Card" style={{ padding: 12 }}>
	            <div className="TileTitle">{siteName ? `Photos — ${siteName}` : 'Photos'}</div>
	            <div className="Muted" style={{ marginTop: 8 }}>
	              {listLoading ? 'Chargement…' : `${items.length} photo(s)`}
	            </div>
              {listLoading ? (
                <GalleryGridSkeleton />
              ) : (
	            <div
	              style={{
	                marginTop: 12,
	                display: 'grid',
	                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
	                gap: 10,
	              }}
		            >
		              {items.map((it) => {
		                const mid = Number(it.media_id);
		                const asset = mediaById.get(String(mid)) || null;
		                const src = Number.isFinite(mid) && mid > 0 ? mediaInlineUrl(apiBaseUrl, Number(siteId), mid) : '';
		                const name = String(asset?.original_name || '').trim() || `Image #${mid}`;
		                return (
		                  <button
	                      key={it.id}
	                      type="button"
	                      className="Tile GalleryTile"
	                      style={{ padding: 10, textAlign: 'left' }}
	                      onClick={() =>
	                        setModal({
	                          type: 'item',
	                          id: Number(it.id),
	                          media_id: Number(it.media_id),
	                          title: String(it.title || ''),
	                          caption: String(it.caption || ''),
                            alt_text: String(it.alt_text || ''),
	                          category_id: it.category_id ? String(it.category_id) : '',
	                          saving: false,
	                          error: '',
	                        })
	                      }
	                      onContextMenu={(e) => openItemMenu(e, it)}
	                    >
	                      {src ? (
	                        <img src={src} alt="" loading="lazy" style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 10 }} />
	                      ) : (
	                        <div className="Muted" style={{ height: 160, display: 'grid', placeItems: 'center' }}>
	                          <Spinner />
	                        </div>
	                      )}
	                      <div className="OneLine" style={{ marginTop: 8, fontWeight: 650 }} title={name}>
	                        {name}
	                      </div>
	                    </button>
		                );
		              })}
		            </div>
              )}
	          </div>
	        </div>
      ) : null}

      {modal?.type === 'pick-upload-category' ? (
        <Modal title="Catégorie" icon={<IconFolder />} onClose={() => setModal(null)}>
          <div className="Muted" style={{ marginBottom: 10 }}>
            Choisis la catégorie pour ces photos.
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className="Button Button--ghost"
                onClick={() => {
                  const files = modal.files || [];
                  setModal(null);
                  uploadToGallery(files, Number(c.id));
                }}
              >
                {c.name}
              </button>
            ))}
            <button type="button" className="Button" onClick={() => setModal({ type: 'new-category', name: '', saving: false, error: '' })}>
              <span className="ButtonInline">
                <IconPlus /> Créer une catégorie
              </span>
            </button>
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'new-category' ? (
        <Modal
          title="Nouvelle catégorie"
          icon={<IconPlus />}
          onClose={() => setModal(null)}
          submitOnEnter
          onSubmit={async () => {
            if (modal.saving) return;
            setModal((m) => ({ ...m, saving: true, error: '' }));
            const res = await createCategory(modal.name);
            if (!res.ok) {
              setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
              return;
            }
            setModal(null);
          }}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving}
                onClick={async () => {
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const res = await createCategory(modal.name);
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                    return;
                  }
                  setModal(null);
                }}
              >
                {modal.saving ? 'Création…' : 'Créer'}
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label">Nom</label>
            <input className="Input" value={modal.name} onChange={(e) => setModal((m) => ({ ...m, name: e.target.value }))} />
          </div>
          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'db-status' ? (
        <GalleryDbStatusModal
          siteId={Number(siteId)}
          dbConfig={state.dbConfig}
          ensureOk={state.ensureOk}
          ensureError={state.ensureOk ? '' : state.error}
          dbLoading={dbLoading}
          onEnsure={ensureTables}
          onClose={() => setModal(null)}
        />
      ) : null}

      {modal?.type === 'rename-category' ? (
        <Modal
          title="Renommer la catégorie"
          icon={<IconEdit />}
          onClose={() => setModal(null)}
          submitOnEnter
          onSubmit={confirmRenameCategory}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button type="button" className="Button" disabled={modal.saving} onClick={confirmRenameCategory}>
                {modal.saving ? 'Sauvegarde…' : 'Sauvegarder'}
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label">Nom</label>
            <input className="Input" value={modal.name} onChange={(e) => setModal((m) => ({ ...m, name: e.target.value }))} />
          </div>
          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'delete-category' ? (
        <Modal
          title="Supprimer la catégorie ?"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.deleting}>
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--danger"
                disabled={modal.deleting}
                onClick={async () => {
                  if (modal.deleting) return;
                  setModal((m) => (m ? { ...m, deleting: true, error: '' } : m));
                  const res = await api.galleryCategoryDelete({ site_id: Number(siteId), id: Number(modal.id) });
                  if (!res.ok) {
                    setModal((m) => (m ? { ...m, deleting: false, error: res.error || 'Erreur' } : m));
                    return;
                  }
                  if (String(selectedCategoryId) === String(modal.id)) setSelectedCategoryId('');
                  setModal(null);
                  refresh();
                }}
              >
                {modal.deleting ? 'Suppression…' : 'Supprimer'}
              </button>
            </>
          }
        >
          <div className="Muted">Cette action retirera la catégorie et laissera les photos dans “Toutes les photos”.</div>
          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'item' ? (
        <Modal
          title="Photo"
          icon={<IconImage />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Fermer
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving}
                onClick={async () => {
                  if (modal.saving) return;
                  setModal((m) => (m ? { ...m, saving: true, error: '' } : m));
                  const cid = String(modal.category_id || '').trim();
                  const res = await api.galleryItemUpdate({
                    site_id: Number(siteId),
                    id: Number(modal.id),
                    title: String(modal.title || ''),
                    caption: String(modal.caption || ''),
                    alt_text: String(modal.alt_text || ''),
                    category_id: cid ? Number(cid) : null,
                  });
                  if (!res.ok) {
                    setModal((m) => (m ? { ...m, saving: false, error: res.error || 'Erreur' } : m));
                    return;
                  }
                  setModal(null);
                  refresh();
                }}
              >
                {modal.saving ? 'Sauvegarde…' : 'Sauvegarder'}
              </button>
            </>
          }
        >
          {(() => {
            const mid = Number(modal.media_id);
            const src = Number.isFinite(mid) && mid > 0 ? mediaInlineUrl(apiBaseUrl, Number(siteId), mid) : '';
            const asset = mediaById.get(String(mid)) || null;
	            const name = String(asset?.original_name || '').trim() || `Image #${mid}`;
	            return (
	              <div style={{ display: 'grid', gap: 12 }}>
	                {src ? (
	                  <img src={src} alt={String(modal.alt_text || '')} style={{ width: '100%', maxHeight: '52vh', objectFit: 'contain', borderRadius: 12 }} />
	                ) : (
	                  <div className="Muted" style={{ height: 240, display: 'grid', placeItems: 'center' }}>
	                    <Spinner />
	                  </div>
	                )}

                <div className="Muted OneLine" title={name}>
                  {name}
                </div>

                <div className="Field">
                  <label className="Label">Titre</label>
                  <textarea className="Textarea" rows={2} value={modal.title} onChange={(e) => setModal((m) => ({ ...m, title: e.target.value }))} />
                </div>
	                <div className="Field">
	                  <label className="Label">Description</label>
	                  <textarea className="Textarea" rows={4} value={modal.caption} onChange={(e) => setModal((m) => ({ ...m, caption: e.target.value }))} />
	                </div>
                <div className="Field">
                  <label className="Label">Texte alternatif</label>
                  <textarea className="Textarea" rows={3} value={modal.alt_text} onChange={(e) => setModal((m) => ({ ...m, alt_text: e.target.value }))} />
                </div>
	                <div className="Field">
	                  <label className="Label">Catégorie</label>
	                  <select className="Select" value={String(modal.category_id || '')} onChange={(e) => setModal((m) => ({ ...m, category_id: e.target.value }))}>
	                    <option value="">— Aucune —</option>
                    {categories.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                {modal.error ? (
                  <div className="Error" role="alert">
                    {modal.error}
                  </div>
                ) : null}
              </div>
            );
          })()}
        </Modal>
      ) : null}

      <ContextMenu menu={ctxMenu} onClose={closeCtxMenu} />
    </div>
  );
}

function GalleryDbStatusModal({ siteId, dbConfig, ensureOk, ensureError, dbLoading = false, onEnsure, onClose }) {
  const [ensuring, setEnsuring] = useState(false);
  const [ensureLocalError, setEnsureLocalError] = useState('');
  const [test, setTest] = useState({ status: 'idle', error: '' }); // idle | loading | ok | error

  useEffect(() => {
    let cancelled = false;
    if (!dbConfig || !ensureOk) {
      setTest({ status: 'idle', error: '' });
      return () => {};
    }
    setTest({ status: 'loading', error: '' });
    api
      .galleryDbTest({ site_id: Number(siteId), use_stored: true })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setTest({ status: 'error', error: res.error || 'Connexion impossible' });
          return;
        }
        setTest({ status: 'ok', error: '' });
      })
      .catch(() => {
        if (cancelled) return;
        setTest({ status: 'error', error: 'Connexion impossible' });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, dbConfig, ensureOk]);

  const step1 = dbLoading ? 'pending' : dbConfig ? 'ok' : 'bad';
  const step2 = step1 === 'ok' ? (ensureOk ? 'ok' : 'bad') : 'disabled';
  const step3 =
    step2 === 'ok'
      ? test.status === 'ok'
        ? 'ok'
        : test.status === 'error'
        ? 'bad'
        : test.status === 'loading'
        ? 'pending'
        : 'pending'
      : 'disabled';

  return (
    <Modal
      title="BDD Galerie"
      icon={step2 === 'ok' && step3 === 'ok' ? <IconCheck /> : <IconAlertTriangle />}
      onClose={onClose}
      footer={
        <button type="button" className="Button Button--ghost" onClick={onClose}>
          Fermer
        </button>
      }
    >
      <DbSteps
        steps={[
          {
            key: 'db',
            title: 'Base de données',
            status: step1,
            detail: dbConfig
              ? `${dbConfig.host}:${dbConfig.port} · ${dbConfig.db_name}`
              : dbLoading
              ? <Skeleton variant="line" style={{ width: 220, marginTop: 2 }} />
              : 'Aucune base configurée pour ce site.',
            actions: (
              <a className="Button Button--ghost" href="#/databases">
                <span className="ButtonInline">
                  <IconGlobe /> Ouvrir Bases de données
                </span>
              </a>
            ),
          },
          {
            key: 'tables',
            title: 'Tables Galerie',
            status: step2,
            detail: step2 === 'ok' ? 'OK' : step2 === 'bad' ? ensureLocalError || ensureError || 'Tables manquantes' : '—',
            actions: dbConfig ? (
              <button
                type="button"
                className="Button"
                disabled={ensuring || dbLoading}
                onClick={async () => {
                  if (ensuring) return;
                  setEnsuring(true);
                  setEnsureLocalError('');
                  const res = await onEnsure?.();
                  if (!res?.ok) {
                    setEnsureLocalError(res?.error || 'Erreur');
                    setEnsuring(false);
                    return;
                  }
                  setEnsuring(false);
                }}
              >
                <span className="ButtonInline">
                  <IconPlus /> {ensuring ? 'Vérification…' : 'Initialiser / Vérifier'}
                </span>
              </button>
            ) : null,
          },
          {
            key: 'conn',
            title: 'Connexion',
            status: step3,
            detail:
              step3 === 'ok'
                ? 'Connecté'
                : step3 === 'bad'
                ? test.error || 'Connexion impossible'
                : step3 === 'pending'
                ? 'Test en cours…'
                : '—',
          },
        ]}
      />
    </Modal>
  );
}
